import { useState } from 'react';
import { Link } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { ViewSidebar } from '@/components/ViewSidebar';
import { Button } from '@/components/Button';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { money, qty } from '@/lib/format';
import { Applied, DataTable, Denied, FilterButton, k, Pager, PhoneBar, SearchBox, Sentinel, type Col } from './ListKit';
import { FilterSheet } from './FilterSheet';
import { Avail } from './StockCards';
import { usePagedList, useUrlState, useWarehouses } from './api';
import type { WarehouseStock } from './types';

/** One row per product and warehouse, using the existing read-only stock endpoint. */
export function WarehouseStocksPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const allowed = can('inventory.read');
  const [sp, set] = useUrlState();
  const q = sp.get('q') || '';
  const warehouseId = sp.get('warehouseId') || undefined;
  const page = Number(sp.get('page')) || 1;
  const phone = ff === 'phone';
  const pageSize = phone ? 20 : 50;
  const [panel, setPanel] = useState(false);
  const warehouses = useWarehouses(allowed);
  const list = usePagedList<WarehouseStock>(allowed ? '/api/inventory/stocks' : null,
    { keyword: q || undefined, warehouseId }, { pageSize, append: phone, page });

  if (!allowed) return <Denied what="查看按仓库明细" perm="查看库存" />;

  const cols: Col<WarehouseStock>[] = [
    { key: 'code', title: '产品编号', w: 136, fix: true, render: (r) => <b className="tnum">{r.product.code}</b> },
    { key: 'name', title: '产品名称', w: 210, render: (r) => r.product.name },
    { key: 'cat', title: '分类', w: 110, render: (r) => r.product.category || '—' },
    { key: 'wh', title: '仓库', w: 160, render: (r) => `${r.warehouse.code} · ${r.warehouse.name}` },
    { key: 'cur', title: '现有', w: 100, align: 'r', render: (r) => qty(r.currentQuantity) },
    { key: 'res', title: '预占', w: 100, align: 'r', render: (r) => qty(r.reservedQuantity) },
    { key: 'av', title: '可用', w: 100, align: 'r', render: (r) => <Avail value={Number(r.availableQuantity)} strong /> },
    { key: 'cost', title: '入库均价', w: 110, align: 'r', render: (r) => r.inventoryUnitCost == null ? '—' : money(r.inventoryUnitCost) },
    { key: 'value', title: '库存价值', w: 130, align: 'r', render: (r) => r.inventoryValue == null ? '—' : money(r.inventoryValue) },
  ];
  const search = <SearchBox value={q} placeholder="产品名称 / 编号" onSearch={(value) => set({ q: value || undefined, page: undefined })} />;
  const filter = <FilterButton boxed count={Number(!!warehouseId)} onClick={() => setPanel(true)} />;
  const applied = <Applied items={warehouseId ? [{ key: 'warehouseId', label: warehouses.find((w) => w.id === warehouseId)?.name || '指定仓库', onRemove: () => set({ warehouseId: undefined, page: undefined }) }] : []} onClear={() => set({ warehouseId: undefined, page: undefined })} />;
  const filterSheet = panel && <FilterSheet sheet={phone} value={{ warehouseId }} onClose={() => setPanel(false)}
    onApply={(value) => { setPanel(false); set({ warehouseId: value.warehouseId, page: undefined }); }}
    groups={[{ key: 'warehouseId', title: '仓库', opts: warehouses.map((w) => ({ value: w.id, label: w.name })) }]} />;
  const body = list.error && !list.items.length
    ? <ErrorState title="仓库库存加载失败" message={list.error.message} detail="GET /api/inventory/stocks" onRetry={list.reload} />
    : list.loading && !list.items.length
      ? <div className={k.pad}><Skeleton lines={3} /><Skeleton lines={3} /></div>
      : !list.items.length
        ? <Empty icon="box" title="没有符合条件的库存明细">换个关键词或仓库再看看。</Empty>
        : null;
  const table = <DataTable cols={cols} rows={list.items} rowKey={(r) => r.id} />;
  const pager = !body && <Pager page={list.page} pageSize={pageSize} total={list.total} shown={list.items.length} totalPages={list.totalPages} onGo={(p) => set({ page: p > 1 ? String(p) : undefined })} />;

  if (ff === 'desktop') return <div className={`${k.desk} ${k.desktopPage}`}>
    <header className={k.desktopHead}><div><h1>按仓库明细</h1><p>每个产品在每个仓库单独一行<span className="tnum">共 {list.total} 条</span></p></div><div className={k.desktopActions}><Button icon="ref" onClick={list.reload}>刷新</Button></div></header>
    <div className={k.desktopWork}>
      <ViewSidebar title="视图" active="warehouse" onPick={() => undefined} views={[
        { key: 'all', label: '库存总览', icon: 'box', to: '/inventory' },
        { key: 'warehouse', label: '按仓库明细', icon: 'bank', count: list.total },
        { key: 'change', label: '变动汇总', icon: 'chart', to: '/inventory/changes' },
        { key: 'shortage', label: '负可用', icon: 'alert', to: '/inventory/shortage', separator: true },
        { key: 'transit', label: '在途待入库', icon: 'inbox', ...(can('purchase.read') ? { to: '/inventory/transit' } : { disabledReason: '当前账号没有采购单查看权限' }) },
        { key: 'noStock', label: '无实际库存', icon: 'flag', to: '/inventory?view=noStock' },
        ...(can('inventory.transactions.read') ? [{ key: 'tx', label: '出入库流水', icon: 'book', to: '/inventory/transactions', separator: true }] : []),
        ...(can('imports.read') ? [{ key: 'imports', label: '导入历史', icon: 'down', to: '/imports' }] : []),
      ]} />
      <section className={k.desktopArea}><div className={k.desktopToolbar}>{search}{filter}</div>{applied}{body ? <div className={k.dbody}>{body}</div> : table}{pager}</section>
    </div>{filterSheet}
  </div>;

  if (phone) return <div className={k.phone}>
    <PhoneBar title="按仓库明细" back={{ label: '库存', fallback: '/inventory' }} />
    <div className={k.chipbar}><div className={k.chips}><Link className={`${k.chip} ${k.navChip}`} to="/inventory">库存总览</Link><span className={`${k.chip} ${k.on}`}>按仓库明细</span></div></div>
    <div className={k.psearch}>{search}</div>{applied}
    <div className={k.pbody}><div className={k.listbar}><span>{list.total} 条产品 × 仓库记录</span><FilterButton count={Number(!!warehouseId)} onClick={() => setPanel(true)} /></div>
      {body || <div className={k.pcardTable}>{table}</div>}<Sentinel hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />
    </div>{filterSheet}
  </div>;

  return <div className={k.desk}><header className={k.dh}><h1>按仓库明细<small>{list.total} 条</small></h1><span className={k.sp} />{search}{filter}<Button icon="ref" onClick={list.reload}>刷新</Button><Link className={k.linkBtn} to="/inventory">库存总览</Link></header>{applied}{body ? <div className={k.dbody}>{body}</div> : table}{pager}{filterSheet}</div>;
}
